import { PostureManagementTabs } from '@/components/Inventory/constants';
import { normalizeTabKey } from '@/components/settings/settingsTabs';
import {
  AI_AGENT_INSTRUCTIONS_SLUG,
  EXTENSION_SECURITY_CATALOG_SLUG,
  EXTENSION_SECURITY_CUSTOM_RULES_SLUG,
  POLICIES_AI_INSTRUCTIONS_SLUG,
  POLICIES_ALLOW_BLOCK_SLUG,
  POLICIES_POLICY_SETTINGS_SLUG,
  SECRET_DETECTION_GITLEAKS_RULES_SLUG,
} from '@/lib/policyPaths';
import { OrgTab } from '@/pages/organization/orgTabKeys';

/**
 * Every screen the in-app help knows about. A doc claims screens with its
 * `screen_keys` front matter. The docs CI job reads this list
 * (`apps/docs/scripts/lib/frontend.ts`): it rejects unknown keys in docs, and
 * will require a doc for every key once the docs rollout turns that on
 * (`apps/docs/coverage.json`). Keys name the screen, not the route, so a
 * route rename keeps its key.
 * The organization and personal scopes share a key where the screen is the
 * same.
 */
export const SCREEN_KEYS = [
  'dashboard',
  'inventory-users',
  'inventory-endpoints',
  'inventory-endpoint',
  'inventory-projects',
  'inventory-project',
  'inventory-mcp',
  'inventory-extensions',
  'inventory-extension-scan-result',
  'inventory-plugins',
  'inventory-ai-instructions',
  'inventory-skills',
  'inventory-hooks',
  'shadow-ai-tools',
  'shadow-ai-network-discovery',
  'agents-activity',
  'browser-agent-sites',
  'policies',
  'policies-ai-instructions',
  'policies-allow-block-lists',
  'policies-policy-settings',
  'policies-ai-agent-instructions-catalog',
  'policies-extension-security-catalog',
  'policies-extension-security-custom-rules',
  'policies-secret-detection-rules',
  'secure-ai-coding-enforcement',
  'threat-modeling',
  'llm-vulnerability-discovery',
  'alerts',
  'alerts-session-view',
  'attack-demonstrations',
  'users',
  'security-groups',
  'security-group-detail',
  'settings-organization',
  'settings-workspace',
  'settings-integrations',
  'settings-notifications',
  'settings-preferences',
  'audit-logs',
  'get-started',
] as const;

export type ScreenKey = (typeof SCREEN_KEYS)[number];

/** The personal workspace lives under `/dashboard/…`; organizations under `/:organizationId/…`. */
const PERSONAL_SCOPE = 'dashboard';

const INVENTORY_TABS: Record<string, ScreenKey> = {
  [PostureManagementTabs.USER_SECURITY_POSTURE]: 'inventory-users',
  [PostureManagementTabs.ENDPOINTS]: 'inventory-endpoints',
  [PostureManagementTabs.PROJECTS]: 'inventory-projects',
  [PostureManagementTabs.MCP]: 'inventory-mcp',
  [PostureManagementTabs.EXTENSIONS]: 'inventory-extensions',
  [PostureManagementTabs.PLUGINS]: 'inventory-plugins',
  [PostureManagementTabs.AI_INSTRUCTIONS]: 'inventory-ai-instructions',
  [PostureManagementTabs.SKILLS]: 'inventory-skills',
  [PostureManagementTabs.HOOKS]: 'inventory-hooks',
};

const POLICY_PAGES: Record<string, ScreenKey> = {
  [POLICIES_AI_INSTRUCTIONS_SLUG]: 'policies-ai-instructions',
  [POLICIES_ALLOW_BLOCK_SLUG]: 'policies-allow-block-lists',
  [POLICIES_POLICY_SETTINGS_SLUG]: 'policies-policy-settings',
  [AI_AGENT_INSTRUCTIONS_SLUG]: 'policies-ai-agent-instructions-catalog',
  [EXTENSION_SECURITY_CATALOG_SLUG]: 'policies-extension-security-catalog',
  [EXTENSION_SECURITY_CUSTOM_RULES_SLUG]:
    'policies-extension-security-custom-rules',
  [SECRET_DETECTION_GITLEAKS_RULES_SLUG]: 'policies-secret-detection-rules',
};

function lookup(
  table: Record<string, ScreenKey>,
  key: string | null | undefined
): ScreenKey | null {
  return key && Object.hasOwn(table, key) ? table[key] : null;
}

function inventoryScreen(
  rest: string[],
  tabParam: string | null
): ScreenKey | null {
  if (rest[0] === 'projects' && rest[1]) return 'inventory-project';
  // The endpoint page and the endpoint-ID lookup that opens it.
  if (rest[0] === 'endpoints' && rest[1]) return 'inventory-endpoint';
  if (rest[0] === 'extensions' && rest[2] === 'scan-result') {
    return 'inventory-extension-scan-result';
  }
  // Without a known ?tab= the page is about to write its default tab into
  // the URL, which resolves on the next render.
  return lookup(INVENTORY_TABS, tabParam);
}

/**
 * The screen the user is on, from the current location, or null for a path
 * that is not a screen (a redirect, or a page the help does not cover).
 */
export function resolveDocKey(pathname: string, search = ''): ScreenKey | null {
  const [scope, tab, ...rest] = pathname.split('/').filter(Boolean);
  if (!scope || !tab) return null;
  const personal = scope === PERSONAL_SCOPE;
  const tabParam = new URLSearchParams(search).get('tab');

  switch (tab) {
    case OrgTab.Dashboard:
      return personal ? null : 'dashboard';
    case OrgTab.PostureManagement:
      return inventoryScreen(rest, tabParam);
    case OrgTab.ShadowAI:
      return rest[0] === 'network-discovery'
        ? 'shadow-ai-network-discovery'
        : 'shadow-ai-tools';
    case OrgTab.AgentsActivity:
      return rest[0] === 'browser-agent-sites'
        ? 'browser-agent-sites'
        : 'agents-activity';
    case OrgTab.Policies:
      return rest[0] ? lookup(POLICY_PAGES, rest[0]) : 'policies';
    case OrgTab.SecureAiCoding:
      return 'secure-ai-coding-enforcement';
    case OrgTab.ThreatModeling:
      return 'threat-modeling';
    case OrgTab.LlmVulnDiscovery:
      return 'llm-vulnerability-discovery';
    case OrgTab.Alerts:
      return rest[0] === 'session' ? 'alerts-session-view' : 'alerts';
    case OrgTab.AttackDemonstrations:
      return 'attack-demonstrations';
    case OrgTab.Users:
      return 'users';
    case OrgTab.SecurityGroups:
      return rest[0] ? 'security-group-detail' : 'security-groups';
    case OrgTab.Settings:
      return `settings-${normalizeTabKey(personal ? 'singleton' : 'organization', tabParam)}`;
    case OrgTab.AuditLogs:
      return 'audit-logs';
    case OrgTab.Install:
      return 'get-started';
    default:
      return null;
  }
}
